<template>
  <nav>
    <ul :class="$style.list">
      <li v-for="item in ITEMS" :key="item.id" :class="$style.item">
        <menu-button
          v-auth-required="{ enabled: item.auth }"
          :to="item.to"
          block
          :icon="item.icon"
          :data-menu-id="item.id"
          :class="$style.btn"
          :indicator-count="item.indicator || undefined"
          :label="item.label || undefined"
          v-analytics.click="[
            'LigaPass_Menu_ButtonClick',
            {
              button_name: t(item.translate),
              authorization_flag: isAuthorized
            }
          ]"
        >
          {{ t(item.translate) }}
        </menu-button>
      </li>
    </ul>
  </nav>
</template>

<i18n lang="yaml">
ru:
  Tasks: Задания
  Season Pass: Season Pass
  squads: Сквады
  Fantasy: Fantasy
  pickem: Pick’em
  Shop: Магазин
  News: Новости
  Ratings: Рейтинг
en:
  Tasks: Tasks
  Season Pass: Season Pass
  squads: Squads
  Fantasy: Fantasy
  pickem: PickEm
  Shop: Shop
  News: News
  Ratings: Ratings
</i18n>

<script lang="ts" setup>
import { computed } from 'vue'
import MenuButton from '../menu-button/index.vue'
import { useAccountStore } from '~/stores/account'

const { t } = useI18n()
const accountStore = useAccountStore()
const { isAuthorized } = storeToRefs(accountStore)

interface Item {
  id: string
  to: string
  icon?: string
  translate: string
  indicator?: string
  label?: string
  auth?: boolean
}

const ITEMS = computed<Item[]>(() => {
  const NEWS_INDICATOR_KEY = 'newsIndicatorFirstShown'
  const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000

  const getNewsIndicator = (): string | undefined => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return undefined
    }

    if (localStorage.getItem('readLastNews')) {
      return undefined
    }

    const firstShownStr = localStorage.getItem(NEWS_INDICATOR_KEY)
    const now = Date.now()

    if (!firstShownStr) {
      localStorage.setItem(NEWS_INDICATOR_KEY, now.toString())
      return '1'
    }

    const firstShown = parseInt(firstShownStr, 10)
    const daysPassed = now - firstShown

    if (daysPassed >= TWO_DAYS_MS) {
      return undefined
    }
    return '1'
  }

  return [
    {
      id: 'tasks',
      to: '/',
      icon: 'tasks',
      translate: 'Tasks'
    },
    {
      id: 'battlepass',
      to: '/battlepass',
      icon: 'seasonpass',
      translate: 'Season Pass'
    },
    {
      id: 'squads',
      to: '/squads',
      icon: 'people',
      translate: 'squads'
    },
    {
      id: 'fantasy',
      to: '/fantasy',
      icon: 'swords',
      translate: 'Fantasy'
    },
    {
      id: 'pickem',
      to: '/pickem',
      icon: 'darts-filled',
      translate: 'pickem'
    },
    {
      id: 'shop',
      to: '/shop',
      icon: 'store',
      translate: 'Shop'
    },
    {
      id: 'news',
      to: '/news',
      icon: 'news',
      indicator: getNewsIndicator(),
      translate: 'News'
    },
    {
      id: 'ratings',
      to: '/ratings',
      icon: 'trophy',
      translate: 'Ratings'
    }
  ]
})
</script>

<style lang="scss" module>
.list {
  display: flex;
  flex-direction: column;
  gap: 1.4em;
}

.item {
  display: contents;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
